import { useEffect, useRef } from 'react';

import { useSelector } from 'react-redux';

import { useBarcode } from '~/modules/devices/hooks/useBarcode';
import { getUserHasFullAccess, getUserPlanIsLoaded } from '~/selectors/plans.selectors';
import { newRelic } from '~/utils/newRelic/newRelic';

import { SCAN_EVENT_QUEUE } from '../constants/scanEventQueue.const';
import { createScanBarcodeContent } from '../utils/createScanBarcodeContent.util';
import { createScanContext } from '../utils/createScanContext.util';
import { createScanEventProcessor } from '../utils/createScanEventProcessor.util';
import { createScanActionHandler } from '../utils/scanActionHandler.util';

/**
 * Mounts the sole consumer of the scan event queue.
 */
export const useScanEventProcessor = () => {
  const { resolveBarcode } = useBarcode();
  const hasFullAccess = useSelector(getUserHasFullAccess);
  const isPlanLoaded = useSelector(getUserPlanIsLoaded);
  const hasFullAccessRef = useRef(hasFullAccess);
  hasFullAccessRef.current = hasFullAccess;

  useEffect(() => {
    if (!isPlanLoaded) return;

    const scanActionHandler = createScanActionHandler();

    const processor = createScanEventProcessor({
      canProcessScanEvent: () => hasFullAccessRef.current,
      getScanContext: (source) =>
        createScanContext({
          ...source,
          getBarcodeContent: (barcodeSource) =>
            createScanBarcodeContent({ ...barcodeSource, resolveBarcode }),
        }),
      handleScanEvent: scanActionHandler.handle,
      queue: SCAN_EVENT_QUEUE,
    });

    return SCAN_EVENT_QUEUE.subscribe(() => {
      processor
        .process()
        .catch((error) => newRelic.noticeError('Failed to drain the scan event queue', { error }));
    });
  }, [isPlanLoaded, resolveBarcode]);
};
